import type { FlowConfigData } from '../types/flowConfig.types'

/**
 * Remove entradas de `ui_layout` cujo id já não existe em `steps`.
 * Devolve a **mesma referência** de `config` quando não há nada a podar (evita re-renders em cadeia).
 */
export function pruneUiLayoutToSteps(config: FlowConfigData): FlowConfigData {
  const ids = new Set(Object.keys(config.steps || {}))
  const raw = config.ui_layout ?? {}
  for (const k of Object.keys(raw)) {
    if (!ids.has(k)) {
      const ui_layout: Record<string, { x: number; y: number }> = {}
      for (const k2 of Object.keys(raw)) {
        if (ids.has(k2)) {
          ui_layout[k2] = raw[k2]!
        }
      }
      return { ...config, ui_layout }
    }
  }
  return config
}

/**
 * Remove uma ou mais etapas do fluxo (ex.: tecla Delete no canvas).
 * Ajusta `start_step` se for removido; poda `ui_layout`.
 */
export function removeStepsFromFlowConfig(
  config: FlowConfigData,
  stepIdsToRemove: ReadonlySet<string>
): FlowConfigData {
  if (stepIdsToRemove.size === 0) return config
  const steps = { ...(config.steps || {}) }
  for (const id of stepIdsToRemove) {
    delete steps[id]
  }
  let start_step = config.start_step
  if (start_step && stepIdsToRemove.has(start_step)) {
    start_step = Object.keys(steps)[0] || ''
  }
  return pruneUiLayoutToSteps({ ...config, steps, start_step })
}

/** Comparação estável para evitar `setState` quando só mudou a referência do objeto. */
export function flowConfigSemanticEqual(a: FlowConfigData, b: FlowConfigData): boolean {
  if (a === b) return true
  try {
    return (
      JSON.stringify({
        name: a.name,
        description: a.description,
        start_step: a.start_step,
        steps: a.steps,
        ui_layout: a.ui_layout
      }) ===
      JSON.stringify({
        name: b.name,
        description: b.description,
        start_step: b.start_step,
        steps: b.steps,
        ui_layout: b.ui_layout
      })
    )
  } catch {
    return false
  }
}

/** Assinatura para o efeito do canvas: só ressincronizar nós/arestas quando o fluxo mudar de facto. */
export function flowConfigCanvasSignature(config: FlowConfigData): string {
  try {
    return JSON.stringify({
      start_step: config.start_step,
      steps: config.steps,
      ui_layout: config.ui_layout
    })
  } catch {
    return String(Date.now())
  }
}
